/* eslint-disable no-magic-numbers */

import Decimal from 'decimal.js';

import {
    subtaskDuration,
    workPaceRatio,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

/**
 * See more in [readme.md](../../../readme.md)
 * @param jobPlan
 * @returns
 */
export const cumulativeStrainIndex = (jobPlan:any) => {
    const subtasks: Array<any> = [];
    jobPlan.forEach((assignment:any) => {
        assignment.subtasks.forEach((subtask:any) => {
            subtasks.push({
                ...subtask,
                assignment,
                rsi: new Decimal(revisedStrainIndex(assignment, subtask)).toNumber(),
                duration: new Decimal(subtaskDuration(assignment, subtask.durationSubtask)).toNumber(),
            });
        });
    });
    const sortedSubtasks = subtasks.sort((a, b) => b.rsi - a.rsi).map((subtask:any, index:number) => ({
        ...subtask,
        sort: index,
    }));

    let result = new Decimal(0);
    sortedSubtasks.forEach((subtask:any) => {
        result = result.plus(deltaCosi(sortedSubtasks, subtask.sort));
    });

    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtasks
 * @param index
 * @returns
 */
const deltaCosi = (subtasks:Array<any>, index:number) =>
    new Decimal(hicosi(subtasks[index])).times(new Decimal(deltaHoursMultiplier(subtasks, index))).toString()
;

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtasks
 * @param index
 * @returns
 */
const deltaHoursMultiplier = (subtasks:Array<any>, index:number) => {
    const hours = hoursMultiplier(subtasks, index);
    const cumulative = hoursMultiplierCumulative(subtasks, index);

    return hours.minus(cumulative).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtasks
 * @param index
 * @returns
 */
const durationCumulative = (subtasks:Array<any>, index:number) => {
    let result = new Decimal(0);
    for (let i = index; i >= 0; i--) {
        result = result.plus(subtasks[i].duration);
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @returns
 */
const durationMultiplier = (assignment:any, subtask:any) => {
    const duration = new Decimal(subtaskDuration(assignment, subtask.durationSubtask));

    let result = new Decimal(0.2);
    if (duration.toNumber() > 0.05) {
        // 0.042*U+0.09*ln(U)+0.477
        result = timeMultiplier(duration);
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtask
 * @returns
 */
const durationPerExertionMultiplier = (subtask:any) => {
    const durationPerExertion = new Decimal(subtask.durationPerExertion);

    // 0.45+0.31*N
    let result = new Decimal(0.45).plus(new Decimal(0.31).times(durationPerExertion));
    if (Number(durationPerExertion) > 60) {
        // 19.17*ln(N)-59.44
        result = new Decimal(19.17).times(durationPerExertion.ln()).minus(59.44);
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @returns
 */
const effortsPerMinuteMultiplier = (assignment:any, subtask:any) => {
    const epm = new Decimal(effortsPerMinutePlanned(assignment, subtask));

    let result = new Decimal(0);
    if (Number(epm) > 90) {
        // 0.00334*M^1.96
        result = new Decimal(0.00334).times(epm).pow(1.96);
    } else {
        // 0.1+0.25*M
        result = new Decimal(0.1).plus(new Decimal(0.25).times(epm));
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @returns
 */
const effortsPerMinutePlanned = (assignment:any, subtask:any) => {
    const effortsPerMinute = new Decimal(subtask.effortsPerMinute);
    const plannedRate = new Decimal(workPaceRatio(assignment));
    const actualRate = new Decimal(assignment.trial.actualRate);
    return effortsPerMinute.times(plannedRate).div(actualRate).toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtask
 * @returns
 */
const hicosi = (subtask:any) => {
    const duration = durationMultiplier(subtask.assignment, subtask);
    let result = new Decimal(0);
    if (duration.toNumber() > 0) {
        const rsi = new Decimal(revisedStrainIndex(subtask.assignment, subtask));
        result = rsi.div(duration);
    }

    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtasks
 * @param index
 * @returns
 */
const hoursMultiplier = (subtasks:Array<any>, index:number) => {
    const hours = durationCumulative(subtasks, index);
    let result = new Decimal(0.2);
    if (hours.toNumber() > 0.05) {
        result = timeMultiplier(hours);
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtasks
 * @param index
 * @returns
 */
const hoursMultiplierCumulative = (subtasks:Array<any>, index:number) => {
    const hours = durationCumulative(subtasks, index - 1);
    let result = new Decimal(0);
    if (index > 0) {
        if (hours.toNumber() > 0.05) {
            result = timeMultiplier(hours);
        } else {
            result = new Decimal(0.2);
        }
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtask
 * @returns
 */
const intensityMultiplier = (subtask:any) => {
    const exertionIntensity = new Decimal(subtask.exertionIntensity);

    let exertion;
    let recovery;
    let intensity;
    if (exertionIntensity.toNumber() > 0.4) {
        // 36*L^3-33.3*L^2+24.77*L-1.86
        exertion = new Decimal(36).times(exertionIntensity.pow(3));
        recovery = new Decimal(33.3).times(exertionIntensity.pow(2));
        intensity = new Decimal(24.77).times(exertionIntensity).minus(1.86);
    } else {
        // 30*L^3-15.6*L^2+13*L+0.4
        exertion = new Decimal(30).times(exertionIntensity.pow(3));
        recovery = new Decimal(15.6).times(exertionIntensity.pow(2));
        intensity = new Decimal(13).times(exertionIntensity).plus(0.4);
    }

    return exertion.minus(recovery).plus(intensity);
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param subtask
 * @returns
 */
const postureMultiplier = (subtask:any) => {
    const posture = new Decimal(subtask.posture);

    let result = new Decimal(0);
    if (posture.toNumber() < 0) {
        // 1.2*e^(-0.009*O)-0.2
        const e = new Decimal(-0.009).times(posture).exp();
        result = new Decimal(1.2).times(e).minus(0.2);
    } else if (posture.toNumber() > 30) {
        // 1+0.00028*(O-30)^2
        result = new Decimal(1).plus(new Decimal(0.00028).times(posture.minus(30).pow(2)));
    } else {
        result = new Decimal(1);
    }

    return result;
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param assignment
 * @param subtask
 * @returns
 */
export const revisedStrainIndex = (assignment:any, subtask:any) => {
    let result = new Decimal(0);
    const subtaskDurationHours = new Decimal(subtaskDuration(assignment, subtask.durationSubtask));
    if (subtaskDurationHours.toNumber() > 0) {
        result = intensityMultiplier(subtask)
            .times(effortsPerMinuteMultiplier(assignment, subtask))
            .times(durationPerExertionMultiplier(subtask))
            .times(postureMultiplier(subtask))
            .times(durationMultiplier(assignment, subtask));
    }

    return result.toString();
};

/**
 * See more in [readme.md](../../../readme.md)
 * @param time
 * @returns
 */
// 0.042*U+0.09*ln(U)+0.477
const timeMultiplier = (time:Decimal) => new Decimal(0.042).times(time).plus(new Decimal(0.09).times(time.ln())).plus(new Decimal(0.477));